import { useRef, useCallback } from "react";
import axios from "axios";
import { Device } from "mediasoup-client";
import { getAuthToken } from "../../../utils/getAuthToken";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

async function authPost(path, body) {
  const token = await getAuthToken();
  const res = await axios.post(`${SERVER_DOMAIN}${path}`, body, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
}

async function authGet(path) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}

/**
 * Owns the mediasoup-client side of an SFU-bridged call — the browser never
 * negotiates WebRTC with Meta directly in this mode (calling.sfuBridge.js on
 * the server does that instead); this hook only joins the mediasoup Router
 * as a Producer (mic) + Consumer (caller audio) once the server side is
 * already connected. Deliberately separate from useCallConnection.js rather
 * than merged into it: that hook owns the ring/claim/ended socket-event
 * state machine that's identical regardless of bridge mode, and duplicating
 * that here would be exactly the kind of two-places-to-keep-in-sync bug this
 * codebase has already been burned by (see its own callId-scoping comments).
 * useCallConnection calls into this one only for the media-establishment
 * step, for both the ring-and-accept flow and the already-connected
 * auto-accept "Join" flow.
 */
export default function useSfuCallConnection() {
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const micTrackRef = useRef(null);
  const remoteAudioElRef = useRef(null);

  const ensureRemoteAudioEl = () => {
    if (!remoteAudioElRef.current) {
      const el = document.createElement("audio");
      el.autoplay = true;
      document.body.appendChild(el);
      remoteAudioElRef.current = el;
    }
    return remoteAudioElRef.current;
  };

  const createTransport = useCallback(async (callId, device, direction) => {
    const params = await authPost(`/api/calling/sfu/${callId}/transport`, {});
    const transport = direction === "send" ? device.createSendTransport(params) : device.createRecvTransport(params);

    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      authPost(`/api/calling/sfu/${callId}/transport/${transport.id}/connect`, { dtlsParameters })
        .then(() => callback())
        .catch(errback);
    });

    if (direction === "send") {
      transport.on("produce", ({ kind, rtpParameters }, callback, errback) => {
        authPost(`/api/calling/sfu/${callId}/transport/${transport.id}/produce`, { kind, rtpParameters })
          .then(({ id }) => callback({ id }))
          .catch(errback);
      });
    }

    return transport;
  }, []);

  /**
   * Only meaningful to call once the server has already told Meta accept —
   * either just now (ring mode, right after POST /answer resolves) or
   * however long ago (auto mode, an agent joining a call already in
   * progress) — either way there's no "mute until accept" dance needed here
   * the way the direct-mode hook has: Meta has already accepted by the time
   * this ever runs, so there's no risk of the mic clipping the caller's
   * first words.
   */
  const joinSfuCall = useCallback(
    async (callId) => {
      const { rtpCapabilities } = await authGet(`/api/calling/sfu/${callId}/rtp-capabilities`);
      const device = new Device();
      await device.load({ routerRtpCapabilities: rtpCapabilities });
      deviceRef.current = device;

      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micTrackRef.current = localStream.getAudioTracks()[0];

      const sendTransport = await createTransport(callId, device, "send");
      sendTransportRef.current = sendTransport;
      await sendTransport.produce({ track: micTrackRef.current });

      const recvTransport = await createTransport(callId, device, "recv");
      recvTransportRef.current = recvTransport;

      // The caller's audio Producer might not exist the instant an agent
      // joins — right after accept, before Meta's first RTP packet has
      // actually arrived at the bridge — so a single-shot 409 here isn't a
      // real failure, just early. Bounded retry instead of surfacing that as
      // an error the agent would have to notice and manually retry.
      let consumeParams = null;
      for (let attempt = 0; attempt < 8; attempt++) {
        try {
          consumeParams = await authPost(`/api/calling/sfu/${callId}/transport/${recvTransport.id}/consume`, {
            rtpCapabilities: device.rtpCapabilities,
          });
          break;
        } catch (err) {
          if (err.response?.status !== 409 || attempt === 7) throw err;
          await new Promise((resolve) => setTimeout(resolve, 750));
        }
      }

      const consumer = await recvTransport.consume(consumeParams);
      const audioEl = ensureRemoteAudioEl();
      audioEl.srcObject = new MediaStream([consumer.track]);
      // The `autoplay` attribute alone isn't reliable here: by the time this
      // runs, several async round trips (capabilities, two transports,
      // produce, consume) have passed since whatever click triggered
      // joinSfuCall, which is often enough for the browser's autoplay policy
      // to silently refuse playback with no visible error anywhere — this
      // was confirmed as the actual cause of "server shows healthy packet
      // flow but the agent hears nothing" on a transfer accept. Explicit
      // .play() surfaces that failure instead of hiding it.
      audioEl.play().catch((err) => console.error("[useSfuCallConnection] audio playback blocked:", err.name, err.message));
    },
    [createTransport]
  );

  const leaveSfuCall = useCallback(() => {
    sendTransportRef.current?.close();
    recvTransportRef.current?.close();
    sendTransportRef.current = null;
    recvTransportRef.current = null;
    deviceRef.current = null;
    if (micTrackRef.current) {
      micTrackRef.current.stop();
      micTrackRef.current = null;
    }
    if (remoteAudioElRef.current) {
      remoteAudioElRef.current.srcObject = null;
    }
  }, []);

  return { joinSfuCall, leaveSfuCall };
}
